
import { StandardDialog } from '@roc/ui';
import { capitalizeOnlyFirstLetter, IndividualPropertyDetails } from '@roc/feature-comp-tool';
import { observer } from 'mobx-react';
import { Button, createStyles, Divider, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { IndividualPropertyImages } from './individualPropertyImages';
import { useStore } from '@roc/client-portal-shared/hooks';
import { formatCurrency } from '@roc/ui/utils';
import { zillow, google, valueOrDash, formatNumberField, formatCurrencyField, getStringOrDefault } from '@roc/feature-valuation-review-v2';
import { segmentAnalytics, SegmentTrackerEventCompTool } from '@roc/feature-utils';
import { PropertyDetail } from './propertyDetail';
import { TransactionHistory } from './transactionsHistory';
import { PropertyFeedback } from './propertyFeedback';
import { AddCircleOutlined } from '@material-ui/icons';
import { PriceField } from './priceField';
import { ComparableProperty } from '../types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    compareButton: {
      textAlign: 'center',
      width: "100%"
    },
    editButton: {
      padding: '10px',
      textAlign: 'center',
    },
    priceField: {
      backgroundColor: '#285391',
      color: '#ffffff',
      borderRadius: '10px',
    },
  })
);


export type IndividualPropertyDialogProps = {
  open?: boolean;
  onClose?: () => void;
  property: ComparableProperty;
};

export const PropertyViewModal = observer((props: IndividualPropertyDialogProps) => {
  const { open, onClose, property } = props;
  const { compToolStoreV2: compToolStore } = useStore();
  const { isInternal, internalUsername, userStore, subjectProperty } = compToolStore;
  const classes = useStyles();

  if (!property) {
    return null;
  }
  const { data } = property;

  const getConfidenceTag = (): string => {
    if (confidentValue.includes(data.condition_confidence_tag)) {
      return 'Confident'
    } else if (nonConfidentValue.includes(data.condition_confidence_tag)) {
      return 'Not Confident'
    }
    return null;
  }

  const dialogContent = (
    <Grid container spacing={2}>
      <IndividualPropertyImages property={property} />
      <Grid item md={4} xs={12} className={classes.priceField}>
        <PriceField property={property} />
      </Grid>
      <PropertyDetail label="Rm" value={valueOrDash(data.rooms)} addArrow={!property.is_subject} field="rooms" lg={2} md={3} variant={"h3"} />
      <PropertyDetail label="Bd" value={valueOrDash(data.bedrooms)} addArrow={!property.is_subject} field="bedrooms" lg={1} md={2} variant={"h3"} />
      <PropertyDetail label="Ba" value={valueOrDash(data.full_bathrooms)} addArrow={!property.is_subject} field="full_bathrooms" lg={2} md={3} variant={"h3"} />
      <PropertyDetail label="HBa" value={valueOrDash(data.half_bathrooms)} addArrow={!property.is_subject} field="half_bathrooms" lg={1} md={2} variant={"h3"} />
      <PropertyDetail label="Parking" value={valueOrDash(data.garage_type)} addArrow={false} field="garage_type" lg={2} md={2} variant={"h3"} />


      <PropertyDetail label="Lot Size" value={formatNumberField(data.lot_size)} addArrow={!property.is_subject} lg={2} md={2} xs={3} variant={"h5"} />
      <PropertyDetail label="Taxes/Yr." value={formatCurrencyField(data.taxes)} addArrow={!property.is_subject} lg={2} md={2} xs={3} variant={"h5"} />
      <PropertyDetail label="HOA" value={formatCurrencyField(data.hoa_fees)} addArrow={!property.is_subject} lg={2} md={2} xs={3} variant={"h5"} />
      <PropertyDetail label="Yr. Built" value={valueOrDash(data.year_built)} lg={1} md={2} xs={3} variant={"h5"} />
      {compToolStore.rentalComps ?
        (<PropertyDetail label="Rent/Sq.Ft." value={formatCurrencyField(data.sqft_rent)} lg={2} md={2} xs={3} variant={"h5"} />) :
        (<PropertyDetail label="Price/Sq.Ft" value={formatCurrencyField(data.sqft_price)} lg={2} md={2} xs={3} variant={"h5"} />)
      }
      <PropertyDetail label="Sq.Ft." value={formatNumberField(data.area)} lg={1} md={2} xs={3} variant={"h5"} />
      <PropertyDetail label="Sale Type" value={valueOrDash(capitalizeOnlyFirstLetter(data.sale_type))} lg={2} md={2} xs={3} variant={"h5"} />


      <PropertyDetail label="Condition" value={getStringOrDefault(capitalizeOnlyFirstLetter(data.condition_tag))} lg={2} md={2} xs={3} variant={"h5"} />
      <PropertyDetail label="Property Type" showTooltip={true} value={getStringOrDefault(capitalizeOnlyFirstLetter(data.property_type))} lg={2} md={2} xs={3} variant={"h5"} />
      <PropertyDetail label="Subject Zoning" value={getStringOrDefault(capitalizeOnlyFirstLetter(data.zoning))} lg={2} md={2} xs={3} variant={"h5"} />
      <PropertyDetail label="Property Status" value={data.is_active ? "Listed on MLS" : "Off Market"} lg={2} md={2} xs={3} variant={"h5"} />
      <PropertyDetail label="Original List Date" value={data.original_list_date} lg={2} md={2} xs={3} variant={"h5"} />
      <PropertyDetail label="Original List Price" value={formatCurrencyField(data.original_list_price)} lg={2} md={2} xs={3} variant={"h5"} />

      <Grid item xs={6} md={4} style={{ textAlign: 'center', alignContent: 'center' }}>
        {!property.is_subject && (
          <Button
            variant="outlined"
            color="primary"
            className={classes.compareButton}
            onClick={() => {
              if (property.added_manually_comparable)
                compToolStore.addManuallyAddedProperties(property)
              else
                compToolStore.addSelectCompareProperties(property)
            }}
            startIcon={<AddCircleOutlined />}
            disabled={compToolStore.selectedCompareProperties.includes(property)}>ADD TO COMPARE
          </Button>
        )}
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>
      <IndividualPropertyDetails property={property} />
      <Grid item xs={12} sm={3} style={{ textAlign: 'right' }}>
        <Button variant='text'>
          <img src={google} alt="Google logo" style={{ height: '30px', width: '30px', margin: '10px' }} onClick={() => {
            segmentAnalytics.trackEvent({
              name: SegmentTrackerEventCompTool.COMP_TOOL_OPEN_GOOGLE,
              userName: isInternal ? internalUsername : userStore.userInformation?.username,
              subjectAddress: subjectProperty.data.address,
              propertyAddress: property.data.address
            });
            window.open(`http://www.google.com/search?q=${data.address}`)
          }} />
        </Button>

        {data.zillow && (
          <Button variant='text'>
            <img src={zillow} alt="Zillow logo" style={{ height: '30px', width: '30px', margin: '10px' }} onClick={() => {
              segmentAnalytics.trackEvent({
                name: SegmentTrackerEventCompTool.COMP_TOOL_OPEN_ZILLOW,
                userName: isInternal ? internalUsername : userStore.userInformation?.username,
                subjectAddress: subjectProperty.data.address,
                propertyAddress: property.data.address
              });
              window.open(data.zillow)
            }} />
          </Button>)
        }
      </Grid>
      {
        (data.condition_tag || data.condition_confidence_tag) && (
          <>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} style={{ paddingBottom: '0px' }} >
              <Typography align="left" variant="subtitle1" color="textSecondary">
                Property Condition Info Based on MLS Comment
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} style={{ paddingBottom: '0px' }} >
              <Typography align="left" variant="body1" color="textSecondary">
                Property Condition - <strong>{data.condition_tag}</strong>
              </Typography>

            </Grid>
            <Grid item xs={12} sm={6} style={{ paddingBottom: '0px' }} >
              <Typography align="right" variant="body1" color="textSecondary">
                Confidence Level of Condition Accuracy - <strong> {getConfidenceTag()}</strong>
              </Typography>
            </Grid>
            {data.condition_explanation &&
              <Grid item xs={12}>
                <Typography align="justify" style={{ fontSize: '16px' }}>
                  {data.condition_explanation}
                </Typography>
              </Grid>
            }

          </>)
      }
      {
        data.comment && (
          <>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} >
              <Typography align="left" variant="subtitle1" color="textSecondary">
                About this home:
              </Typography>
              <Typography align="justify" style={{ fontSize: '16px' }}>
                {data.comment}
              </Typography>
            </Grid>

          </>)
      }
      <Grid item xs={12}>
        <Divider />
      </Grid>
      {data.transactions_history.transactions.length > 0 && (
        <>
          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <TransactionHistory property={property} />
          </Grid>
        </>
      )}
      {
        !property.is_subject && (
          <PropertyFeedback property={property} />
        )
      }
    </Grid>);


  return (
    <StandardDialog
      open={open}
      title={property?.is_subject ? 'Subject Property' : 'Comparable Property'}
      maxWidth="md"
      onClose={onClose}
      handleClose={onClose}
      dialogContent={dialogContent}
      removeContentBox={true}
    />
  );
});

const nonConfidentValue = ['Can try', 'Impossible to understand']

const confidentValue = ['Probable', 'Definitely']

export default PropertyViewModal;
